<template>
    <!-- Add Shop Transaction Modal -->
    <div class="modal fade p-0" id="AddShopTransaction" tabindex="-1" data-bs-focus="false" aria-labelledby="addLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-md">
            <div class="d-flex flex-wrap w-100 position-relative event-auto">
                <div class="card shadow-none">
                    <div class="card-header card-header-primary d-flex flex-wrap align-items-center position-relative">
                        <div class="content-card-left w-100">
                            <h4 class="card-title text-white fs-4">Add Shop Transaction</h4>
                            <p class="fw-light text-white mb-0 pt-1">
                                Record amount against selected salesman & shop
                            </p>
                        </div>
                    </div>

                    <div class="card-body">
                        <Form @submit="onSubmit" :validation-schema="schema" class="custom-form mt-2" ref="myForm">

                            <!-- Salesman -->
                            <div class="position-relative form-group">
                                <Field as="select" name="salesman" class="form-select custom-select" v-model="salesman"
                                    @focus="selectFocus = true" @blur="selectFocus = false">
                                    <option value="0">Choose Salesman</option>
                                    <option v-for="s in salesmenOption" :key="s.id" :value="s.id">
                                        {{ s.first_name }} {{ s.last_name }}
                                    </option>
                                </Field>
                                <label :class="[
                                    selectFocus || salesman !== 0 ? 'focus' : '',
                                    'position-absolute select-label',
                                ]">
                                    Salesman
                                </label>
                                <i class="bi bi-caret-down-fill select-arrow position-absolute"></i>
                                <ErrorMessage class="invalid-message mb-0 pt-2" name="salesman" />
                            </div>

                            <!-- Shop -->
                            <div class="position-relative form-group w-100">
                                <Multiselect v-model="shop" :options="shopOptions"
                                    class="custom-multiselect" placeholder="Select Shop" :searchable="true"
                                    mode="single" label="shopName" track-by="shopName" value-prop="id" />
                                <Field type="hidden" name="shop" v-model="shop" />
                                <ErrorMessage class="invalid-message mb-0 pt-2" name="shop" />
                            </div>

                            <!-- Previous Balance -->
                            <div v-if="prevBalance !== null" class="mb-3">
                                <label class="fw-semibold">Previous Balance:</label>
                                <p class="text-primary mb-0">Rs {{ Number(prevBalance).toFixed(2) }}</p>
                            </div>

                            <!-- Amount -->
                            <div class="position-relative form-group">
                                <Field type="number" name="amount" class="border-input position-relative w-100"
                                    v-model="amount" />
                                <label for="" class="mb-0 placeholder-label w-auto">
                                    Amount
                                </label>
                                <ErrorMessage class="invalid-message mb-0 pt-2" name="amount" />
                            </div>

                            <!-- Amount Type (free-text description of the amount) -->
                            <div class="position-relative form-group">
                                <Field type="text" name="amount_type" class="border-input position-relative w-100"
                                    v-model="amountType" />
                                <label for="" class="mb-0 placeholder-label w-auto">
                                    Amount Type
                                </label>
                                <ErrorMessage class="invalid-message mb-0 pt-2" name="amount_type" />
                            </div>

                            <!-- Submit -->
                            <div
                                class="card-footer custom-pagination d-flex flex-wrap justify-content-between m-0 mb-3 px-4 pb-0 border-0 bg-transparent">
                                <button class="btn btn-primary w-100" type="submit">
                                    <div v-if="loader" class="spinner-border me-2" role="status"></div> Add Record
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import salesmanServices from "@/services/SalesmanServices";
import salesmanShopServices from "@/services/SalesmanShopServices";
import BalanceSheetServices from "@/services/BalanceSheetServices";
import { useRouter } from "vue-router";
export default {
    components: { Multiselect, Form, Field, ErrorMessage },
    setup() {
        const selectFocus = ref(false);
        const salesmenOption = ref([]);
        const salesman = ref(0);
        const shopOptions = ref([]);
        const shop = ref(null);
        const amount = ref(null);
        const amountType = ref("");
        const prevBalance = ref(null);
        const loader = ref(false);
        const router = useRouter();

        // close modal
        const closeModal = () => {
            const myModalEl = document.getElementById("AddShopTransaction");
            const modal = window.bootstrap.Modal.getInstance(myModalEl);
            modal?.hide();
        };

        // validation
        const schema = yup.object().shape({
            salesman: yup.number().moreThan(0, "Salesman is required").typeError("Salesman is required"),
            shop: yup.string().required("Shop is required").typeError("Shop is required"),
            amount: yup
                .number()
                .required("Amount is required")
                .positive("Amount must be greater than zero")
                .typeError("Amount is required"),
            amount_type: yup.string().required("Amount Type is required").typeError("Amount Type is required"),
        });

        // watch salesman to load shops
        watch(salesman, async (newVal) => {
            if (newVal && newVal !== 0) {
                const response = await salesmanShopServices.getSalesmanShops(newVal);
                shopOptions.value = response.data.data;
            } else {
                shopOptions.value = [];
                shop.value = null;
            }
            prevBalance.value = null;
        });

        // watch shop to get previous balance
        watch(shop, async (newVal) => {
            if (newVal && salesman.value) {
                const response = await BalanceSheetServices.getPrevBalance(newVal, salesman.value);
                prevBalance.value = response.data.remaining_amount ?? 0;
            } else {
                prevBalance.value = null;
            }
        });

        // on mount
        onMounted(async () => {
            const res = await salesmanServices.paginateSalesman();
            salesmenOption.value = res.data.data || [];
        });

        // submit
        const onSubmit = async () => {
            loader.value = true;
            try {
                const payload = {
                    salesman_id: salesman.value,
                    shop_id: shop.value,
                    amount: Number(amount.value),
                    amount_type: amountType.value,
                };
                await BalanceSheetServices.addSimpleTransaction(payload);
                loader.value = false;
                router.push("/balancesheet/shop");
                closeModal();
            } catch (err) {
                loader.value = false;
                const msg =
                    err?.response?.data?.message || "Unable to add transaction";
                alert(msg);
            }
        };

        return {
            selectFocus,
            salesmenOption,
            salesman,
            shopOptions,
            shop,
            amount,
            amountType,
            prevBalance,
            loader,
            schema,
            onSubmit,
            closeModal,
        };
    },
};
</script>
